import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

export const VENUE_ADDRESS_LOCAL_FORMAT_COUNTRIES = [
  'Thailand',
  'China',
  'Vietnam',
  'Philippines',
  'India',
  'Nepal',
  'Uzbekistan',
] as const;

export type VenueAddressCountryName = (typeof VENUE_ADDRESS_LOCAL_FORMAT_COUNTRIES)[number];
export type VenueAddressPartKey = 'addressLine1' | 'subLocality' | 'locality' | 'adminArea';

export interface VenueAddressPartFormat {
  key: VenueAddressPartKey;
  label: MessageDescriptor;
  required: boolean;
}

export interface VenueAddressFormat {
  /** Inputs in the order this country commonly writes them. */
  parts: readonly VenueAddressPartFormat[];
  /** Whether the composed value begins with the country name. */
  countryFirst: boolean;
}

export const VENUE_ADDRESS_PART_MAX_LENGTH = 120;

export type VenueAddressPartError = 'required' | 'tooLong' | 'singleLine';

export const VENUE_ADDRESS_PART_ERROR_MESSAGES: Record<VenueAddressPartError, MessageDescriptor> = {
  required: msg`This field is required.`,
  tooLong: msg`This value is too long.`,
  singleLine: msg`Keep this on one line.`,
};

export const VENUE_ADDRESS_GENERIC_FORMAT: VenueAddressFormat = {
  countryFirst: false,
  parts: [
    { key: 'addressLine1', label: msg`Street address`, required: true },
    { key: 'subLocality', label: msg`Neighbourhood / district`, required: false },
    { key: 'locality', label: msg`City / town`, required: true },
    { key: 'adminArea', label: msg`State / province / region`, required: false },
  ],
};

export const VENUE_ADDRESS_FORMATS: Record<VenueAddressCountryName, VenueAddressFormat> = {
  Thailand: {
    countryFirst: false,
    parts: [
      { key: 'addressLine1', label: msg`House no., building, street`, required: true },
      { key: 'subLocality', label: msg`Sub-district (tambon / khwaeng)`, required: true },
      { key: 'locality', label: msg`District (amphoe / khet)`, required: true },
      { key: 'adminArea', label: msg`Province`, required: true },
    ],
  },
  China: {
    countryFirst: true,
    parts: [
      { key: 'adminArea', label: msg`Province`, required: true },
      { key: 'locality', label: msg`City`, required: true },
      { key: 'subLocality', label: msg`District`, required: true },
      { key: 'addressLine1', label: msg`Street address`, required: true },
    ],
  },
  Vietnam: {
    countryFirst: false,
    parts: [
      { key: 'addressLine1', label: msg`House no., street`, required: true },
      { key: 'subLocality', label: msg`Ward / commune (phường / xã)`, required: true },
      { key: 'adminArea', label: msg`Province / city`, required: true },
    ],
  },
  Philippines: {
    countryFirst: false,
    parts: [
      { key: 'addressLine1', label: msg`House no., building, street`, required: true },
      { key: 'subLocality', label: msg`Barangay`, required: true },
      { key: 'locality', label: msg`City / municipality`, required: true },
      { key: 'adminArea', label: msg`Province`, required: true },
    ],
  },
  India: {
    countryFirst: false,
    parts: [
      { key: 'addressLine1', label: msg`Building, street`, required: true },
      { key: 'subLocality', label: msg`Locality / area`, required: true },
      { key: 'locality', label: msg`City / district`, required: true },
      { key: 'adminArea', label: msg`State`, required: true },
    ],
  },
  Nepal: {
    countryFirst: false,
    parts: [
      { key: 'addressLine1', label: msg`Street / tole`, required: true },
      { key: 'subLocality', label: msg`Ward no.`, required: true },
      { key: 'locality', label: msg`Municipality / rural municipality`, required: true },
      { key: 'adminArea', label: msg`District`, required: true },
    ],
  },
  Uzbekistan: {
    countryFirst: false,
    parts: [
      { key: 'addressLine1', label: msg`House no., street`, required: true },
      { key: 'locality', label: msg`City / district (tuman)`, required: true },
      { key: 'adminArea', label: msg`Region (viloyat)`, required: true },
    ],
  },
};
